import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaCincoM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11)); // landscape-ish
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // data from opts or props
      const dataLeft = opts.leftSide || props.leftSide || {};
      const dataRight = opts.rightSide || props.rightSide || {};
      const resumenTabla = opts.resumenTabla || props.resumenTabla || [];

      // split ratio (left gray / right white)
      const leftRatio = typeof opts.leftRatio === 'number' ? opts.leftRatio : 0.5;
      const leftW = Math.round(width * leftRatio);
      const rightX = leftW;

      // left: gray
      ctx.fillStyle = opts.leftBg || '#ffffff';
      ctx.fillRect(0, 0, leftW, height);

      // right: white
      ctx.fillStyle = opts.rightBg || '#ffffff';
      ctx.fillRect(rightX, 0, width - rightX, height);

      // --- vertical divider (yellow, 40px by default) ---
      const dividerWidth = typeof opts.dividerWidth === 'number' ? opts.dividerWidth : 4;
      const dividerColor = opts.dividerColor || '#7d818d';
      const dividerHalf = Math.round(dividerWidth / 2);
      const dividerX = Math.max(0, rightX - dividerHalf);
      ctx.fillStyle = dividerColor;
      ctx.fillRect(dividerX, 0, dividerWidth, height);

      const wrapText = (c, text, x, y0, maxWidth, lh) => {
        const words = String(text || '').split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxWidth && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      const wrapLines = (c, text, maxWidth) => {
        const words = String(text || '').split(' ');
        const lines = [];
        let line = '';
        for (let i = 0; i < words.length; i++) {
          const test = line ? line + ' ' + words[i] : words[i];
          if (c.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = words[i];
          } else {
            line = test;
          }
        }
        if (line) lines.push(line);
        return lines;
      };

      const drawJustifiedLine = (c, text, x, y, maxWidth) => {
        const words = String(text || '').split(' ');
        if (words.length <= 1) {
          c.fillText(text, x, y);
          return;
        }
        let wordsWidth = 0;
        words.forEach((w) => {
          wordsWidth += c.measureText(w).width;
        });
        const extraSpace = Math.max(0, maxWidth - wordsWidth);
        const gap = extraSpace / (words.length - 1);
        let xPos = x;
        words.forEach((w, idx) => {
          c.fillText(w, xPos, y);
          xPos += c.measureText(w).width + (idx < words.length - 1 ? gap : 0);
        });
      };

      const drawJustifiedLines = (c, lines, x, y0, maxWidth, lh) => {
        let yPos = y0;
        lines.forEach((line, idx) => {
          const isLast = idx === lines.length - 1;
          if (isLast) {
            c.fillText(line, x, yPos);
          } else {
            drawJustifiedLine(c, line, x, yPos, maxWidth);
          }
          yPos += lh;
        });
        return yPos;
      };

      const truncateToWidth = (c, text, maxWidth) => {
        let t = String(text || '');
        if (c.measureText(t).width <= maxWidth) return t;
        let trimmed = t;
        while (trimmed.length > 0 && c.measureText(trimmed + '...').width > maxWidth) {
          trimmed = trimmed.slice(0, -1);
        }
        return trimmed.length > 0 ? trimmed + '...' : '...';
      };

      const drawLabelTwoLinesJustified = (c, text, centerX, y0, maxWidth, lh) => {
        const lines = wrapLines(c, text, maxWidth);
        let useLines = lines.slice(0, 2);
        if (lines.length > 2) {
          useLines[1] = truncateToWidth(c, useLines[1], maxWidth);
        } else if (useLines.length === 2) {
          useLines[1] = truncateToWidth(c, useLines[1], maxWidth);
        } else if (useLines.length === 1) {
          useLines[0] = truncateToWidth(c, useLines[0], maxWidth);
        }
        const xLeft = centerX - Math.round(maxWidth / 2);
        c.textAlign = 'left';
        c.textBaseline = 'top';
        drawJustifiedLines(c, useLines, xLeft, y0, maxWidth, lh);
      };

      const wrapLinesWithFirstIndent = (c, text, maxFirst, maxRest) => {
        const words = String(text || '').split(' ');
        const lines = [];
        let line = '';
        let isFirstLine = maxFirst > 0;
        let lineMax = isFirstLine ? maxFirst : maxRest;
        for (let i = 0; i < words.length; i++) {
          const test = line ? line + ' ' + words[i] : words[i];
          if (c.measureText(test).width > lineMax && line) {
            lines.push({ text: line, isFirstLine });
            line = words[i];
            if (isFirstLine) {
              isFirstLine = false;
              lineMax = maxRest;
            }
          } else {
            line = test;
          }
        }
        if (line) lines.push({ text: line, isFirstLine });
        return lines;
      };

      const drawJustifiedWithFirstIndent = (c, text, xFirst, y0, maxFirst, xRest, maxRest, lh) => {
        let yPos = y0;
        if (maxFirst <= 0) {
          yPos += lh;
        }
        const lines = wrapLinesWithFirstIndent(c, text, maxFirst, maxRest);
        lines.forEach((line, idx) => {
          const isLast = idx === lines.length - 1;
          const lineX = line.isFirstLine ? xFirst : xRest;
          const lineMax = line.isFirstLine ? maxFirst : maxRest;
          if (isLast) {
            c.fillText(line.text, lineX, yPos);
          } else {
            drawJustifiedLine(c, line.text, lineX, yPos, lineMax);
          }
          yPos += lh;
        });
        return yPos;
      };

      // ---------------- LEFT SIDE (vertical grouped bars: previous & current) ----------------
      const leftPadding = typeof opts.leftPadding === 'number' ? opts.leftPadding : 28;
      let leftContentX = leftPadding;
      let leftContentY = 36;

      // small square label (LEFT)
      const squareSize = typeof opts.leftSquareSize === 'number' ? opts.leftSquareSize : 56;
      const sqX = leftPadding;
      const sqY = leftContentY;
      ctx.save();
      ctx.fillStyle = opts.leftSquareBg || '#5fe7ffff';
      ctx.fillRect(sqX, sqY, squareSize, squareSize);
      ctx.fillStyle = opts.leftSquareColor || '#0b1220';
      ctx.font = `${opts.leftSquareFontSize || 22}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(opts.leftSquareText || '1.3', sqX + squareSize / 2, sqY + squareSize / 2);
      ctx.restore();

      leftContentX = sqX + squareSize + (typeof opts.leftSquareGap === 'number' ? opts.leftSquareGap : 12);
      const leftMaxW = Math.max(160, leftW - dividerHalf - leftContentX - leftPadding);

      // titles (LEFT)
      const title = dataLeft.title || opts.leftSide?.title || 'Recepción de población desplazada proveniente de';
      const subTitle = dataLeft.subTitle || opts.leftSide?.subTitle || ' otros departamentos';
      ctx.fillStyle = opts.leftTitleColor || '#0f172a';
      ctx.font = `${opts.leftTitleSize || 30}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const titleLineH = Math.round((opts.leftTitleSize || 22) * 1.4);
      const titleLines = wrapLines(ctx, title, leftMaxW);
      leftContentY = drawJustifiedLines(ctx, titleLines, leftContentX, leftContentY, leftMaxW, titleLineH);

      ctx.fillStyle = opts.leftSubTitleColor || '#0f172a';
      ctx.font = `bold ${opts.leftSubTitleSize || 30}px Inter, Arial, sans-serif`;
      const subLineH = Math.round((opts.leftSubTitleSize || 25) * 1.4);
      const lastTitleLine = titleLines[titleLines.length - 1] || '';
      const lastTitleW = ctx.measureText(lastTitleLine).width;
      const spaceW = ctx.measureText(' ').width;
      const lastLineY = leftContentY - titleLineH;
      const subStartX = leftContentX + lastTitleW + spaceW;
      const subMaxFirst = leftMaxW - (subStartX - leftContentX);
      leftContentY = drawJustifiedWithFirstIndent(
        ctx,
        subTitle,
        subStartX,
        lastLineY,
        subMaxFirst,
        leftContentX,
        leftMaxW,
        subLineH
      );

      leftContentY += 60;

      // paragraph (LEFT)
      const texto = dataLeft.texto || opts.leftSide?.texto || '';
      ctx.fillStyle = opts.leftTextColor || '#334155';
      ctx.font = `${opts.leftTextSize || 25}px Inter, Arial, sans-serif`;
      const leftTextLineH = Math.round((opts.leftTextSize || 25) * 1.5);
      const leftTextoLines = wrapLines(ctx, texto, leftMaxW);
      leftContentY = drawJustifiedLines(ctx, leftTextoLines, leftContentX, leftContentY + 12, leftMaxW, leftTextLineH);

      leftContentY += 100;

      // vertical grouped bar chart: two bars per departamento (mesAnterior + mesActual) - LEFT
      const leftDatosRaw = Array.isArray(dataLeft.datos) ? dataLeft.datos : [];
      const leftDatos = leftDatosRaw
        .slice()
        .sort((a, b) => Number(b?.hogaresUltimoMes || 0) - Number(a?.hogaresUltimoMes || 0))
        .slice(0, 5);
      const drawLeftVerticalBars = (c, items, x, y, w, maxH) => {
        if (!items || !items.length) return y;
        // determine max value across both months
        let maxVal = 0;
        items.forEach(it => {
          const a = Number(it.hogaresUltimoMes || 0);
          const b = Number(it.hogaresMesAnterior || 0);
          maxVal = Math.max(maxVal, a, b);
        });
        if (maxVal <= 0) maxVal = 1;

        // chart area
        const axisBottomPadding = 48;
        const legendH = 0; // legend moved below chart
        const chartH = Math.max(80, Math.min(maxH - axisBottomPadding - legendH, 320));
        const chartTop = y + legendH;
        const chartBottom = chartTop + chartH;
        const n = items.length;

        // spacing: gaps between groups and inner gap between two bars
        const groupGap = Math.max(8, Math.round(w * 0.02));
        const innerGap = Math.max(6, Math.round(groupGap / 2));
        const totalGroupGaps = groupGap * (n + 1);
        const groupAvailableW = Math.max(n, w - totalGroupGaps);
        const groupW = Math.floor(groupAvailableW / n);
        const barW = Math.max(8, Math.floor((groupW - innerGap) / 2));

        // baseline
        c.strokeStyle = opts.leftAxisColor || '#cbd5e1';
        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(x, chartBottom + 0.5);
        c.lineTo(x + w, chartBottom + 0.5);
        c.stroke();

        // grid lines & tick labels (3 ticks)
        c.strokeStyle = opts.leftGridColor || 'rgba(15,23,42,0.06)';
        c.lineWidth = 1;
        c.font = `11px Inter, Arial, sans-serif`;
        c.fillStyle = opts.leftGridLabelColor || '#475569';
        c.textAlign = 'right';
        c.textBaseline = 'middle';
        const ticks = 3;
        for (let t = 0; t <= ticks; t++) {
          const yPos = chartTop + Math.round((chartH * t) / ticks);
          c.beginPath();
          c.moveTo(x, yPos + 0.5);
          c.lineTo(x + w, yPos + 0.5);
          c.stroke();
          // left-side label showing scale
          const val = Math.round(maxVal * (1 - t / ticks));
          c.fillText(String(val), x - 8, yPos);
        }

        // draw groups (two bars per group)
        let px = x + groupGap;
        c.textAlign = 'center';
        for (let i = 0; i < n; i++) {
          const it = items[i];
          const valCurr = Number(it.hogaresUltimoMes || 0);
          const valPrev = Number(it.hogaresMesAnterior || 0);

          const currH = Math.round((valCurr / maxVal) * (chartH - 8));
          const prevH = Math.round((valPrev / maxVal) * (chartH - 8));

          // positions within group
          const bar1X = px + Math.floor((groupW - (barW * 2 + innerGap)) / 2);
          const barPrevX = bar1X;
          const barCurrX = barPrevX + barW + innerGap;
          const barPrevY = chartBottom - prevH;
          const barCurrY = chartBottom - currH;

          // previous bar (behind) - red
          c.fillStyle = opts.leftPreviousColor || '#44abef';
          c.fillRect(barPrevX, barPrevY, barW, prevH);

          // current bar (front) - blue
          c.fillStyle = opts.leftCurrentColor || '#2563eb';
          c.fillRect(barCurrX, barCurrY, barW, currH);

          // value labels above bars (if space)
          c.fillStyle = '#0f172a';
          c.font = `11px Inter, Arial, sans-serif`;
          c.textBaseline = 'bottom';
          // prev value (left)
          c.textAlign = 'center';
          c.fillText(String(valPrev), barPrevX + Math.round(barW / 2), Math.max(barPrevY - 4, barPrevY + 10));
          // curr value (right)
          c.fillText(String(valCurr), barCurrX + Math.round(barW / 2), Math.max(barCurrY - 4, barCurrY + 10));

          // label under group (departamento) - two lines justified within label width
          const label = String(it.departamento || '').trim();
          const labelX = px + Math.round(groupW / 2);
          const labelY = chartBottom + 6;
          const maxLabelW = Math.max(28, groupW - 4);
          const labelLineH = 12;
          drawLabelTwoLinesJustified(c, label, labelX, labelY, maxLabelW, labelLineH);

          px += groupW + groupGap;
        }

        // legend: centered below the chart
        try {
          c.font = `12px Inter, Arial, sans-serif`;
          const labCurr = items[0]?.ultimoMes || 'Actual';
          const labPrev = items[0]?.mesAnterior || 'Anterior';
          const boxW = 12;
          const gapBetween = 24;
          const t1 = String(labCurr);
          const t2 = String(labPrev);
          const t1w = c.measureText(t1).width;
          const t2w = c.measureText(t2).width;
          const totalW = boxW + 4 + t1w + gapBetween + boxW + 4 + t2w;
          const lx = x + Math.round((w - totalW) / 2);
          const ly = chartBottom + 40; // 35px below chart bottom
          // current legend (blue)
          c.fillStyle = opts.leftCurrentColor || '#2563eb';
          c.fillRect(lx, ly - 6, boxW, 12);
          c.fillStyle = opts.leftLegendColor || '#334155';
          c.textAlign = 'left';
          c.textBaseline = 'middle';
          c.fillText(t1, lx + boxW + 4, ly);
          // previous legend (red)
          const lx2 = lx + boxW + 4 + t1w + gapBetween;
          c.fillStyle = opts.leftPreviousColor || '#44abef';
          c.fillRect(lx2, ly - 6, boxW, 12);
          c.fillStyle = opts.leftLegendColor || '#334155';
          c.fillText(t2, lx2 + boxW + 4, ly);
        } catch (e) {
          // ignore any measurement errors
        }

        return chartBottom + axisBottomPadding;
      };

      const chartMaxH = 500;
      leftContentY = drawLeftVerticalBars(ctx, leftDatos, leftContentX, leftContentY, leftMaxW, chartMaxH);

      // caption (LEFT)
      const textoGraficoLeft = dataLeft.textoGrafico || '';
      if (textoGraficoLeft) {
        ctx.fillStyle = opts.leftNoteColor || '#475569';
        ctx.font = `${opts.leftNoteSize || 20}px Inter, Arial, sans-serif`;
        const leftNoteLineH = Math.round((opts.leftNoteSize || 20) * 1.4);
        const leftNoteLines = wrapLines(ctx, textoGraficoLeft, leftMaxW);
        leftContentY = drawJustifiedLines(ctx, leftNoteLines, leftContentX, leftContentY + 50, leftMaxW, leftNoteLineH);
      }

      // ---------------- RIGHT SIDE (vertical bars using same approach) ----------------
      const rightPadding = typeof opts.rightPadding === 'number' ? opts.rightPadding : 20;
      const rightXShifted = rightX + dividerHalf;
      const rightContentX = rightXShifted + rightPadding;
      let rightContentY = 36;
      const rightMaxW = (width - rightXShifted) - rightPadding * 2;

      // --- Right square label (same API as left) ---
      const rightSquareSize = typeof opts.rightSquareSize === 'number' ? opts.rightSquareSize : 56;
      const rightSquareGap = typeof opts.rightSquareGap === 'number' ? opts.rightSquareGap : 12;
      const rsqX = rightContentX;
      const rsqY = rightContentY;
      ctx.save();
      ctx.fillStyle = opts.rightSquareBg || '#5fe7ffff';
      ctx.fillRect(rsqX, rsqY, rightSquareSize, rightSquareSize);
      ctx.fillStyle = opts.rightSquareColor || '#0b1220';
      ctx.font = `${opts.rightSquareFontSize || 22}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(opts.rightSquareText || '1.4', rsqX + rightSquareSize / 2, rsqY + rightSquareSize / 2);
      ctx.restore();

      // shift chart start to the right of the square
      const chartX = rightContentX + rightSquareSize + rightSquareGap;
      // available width for chart area (subtract square + gap)
      const chartAreaW = Math.max(160, rightMaxW - (rightSquareSize + rightSquareGap));

      // right titles (place near the square area; we use chartX for text so it aligns after square)
      const rtitle = dataRight.title || opts.rightSide?.title || 'Municipios más expulsores de';
      const rsub = dataRight.subTitle || opts.rightSide?.subTitle || ' otros departamentos';
      ctx.fillStyle = opts.rightTitleColor || '#0f172a';
      ctx.font = `bold ${opts.rightTitleSize || 30}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const rTitleLineH = Math.round((opts.rightTitleSize || 22) * 1.4);
      const rTitleLines = wrapLines(ctx, rtitle, chartAreaW);
      rightContentY = drawJustifiedLines(ctx, rTitleLines, chartX, rightContentY, chartAreaW, rTitleLineH);

      const lastRTitleLine = rTitleLines[rTitleLines.length - 1] || '';
      const lastRTitleW = ctx.measureText(lastRTitleLine).width;
      const rSpaceW = ctx.measureText(' ').width;
      const lastRLineY = rightContentY - rTitleLineH;
      const rSubStartX = chartX + lastRTitleW + rSpaceW;
      const rSubMaxFirst = chartAreaW - (rSubStartX - chartX);

      ctx.fillStyle = opts.rightSubTitleColor || '#0f172a';
      ctx.font = `bold ${opts.rightSubTitleSize || 30}px Inter, Arial, sans-serif`;
      const rSubLineH = Math.round((opts.rightSubTitleSize || 22) * 1.4);
      rightContentY = drawJustifiedWithFirstIndent(
        ctx,
        rsub,
        rSubStartX,
        lastRLineY,
        rSubMaxFirst,
        chartX,
        chartAreaW,
        rSubLineH
      );
      rightContentY += 30;

      // paragraph (RIGHT) - like left
      const textoRight = dataRight.texto || opts.rightSide?.texto || '';
      ctx.fillStyle = opts.rightTextColor || '#334155';
      ctx.font = `${opts.rightTextSize || 25}px Inter, Arial, sans-serif`;
      const rightTextStartY = rightContentY + 50;
      const rightTextLineH = Math.round((opts.rightTextSize || 25) * 1.5);
      let afterRightTextY = rightTextStartY;
      if (textoRight) {
        const rightTextoLines = wrapLines(ctx, textoRight, chartAreaW);
        afterRightTextY = drawJustifiedLines(ctx, rightTextoLines, chartX, rightTextStartY, chartAreaW, rightTextLineH);
      }

      // right dataset (top 5 municipios by hogares)
      const rightDatosRaw = Array.isArray(dataRight.datos) ? dataRight.datos : [];
      const getRightValue = (it) => Number(
        it?.hogares ?? it?.totalHogares ?? it?.total_hogares ?? it?.hogares_count ?? 0
      );
      const rightDatos = rightDatosRaw
        .slice()
        .sort((a, b) => getRightValue(b) - getRightValue(a))
        .slice(0, 5);

      // draw vertical bars for municipalities (single bar per group) using same styling as earlier
      const drawRightVerticalBars = (c, items, x, y, w, maxH) => {
        if (!items || !items.length) return y;
        // determine max value (use totalHogares)
        let maxVal = 0;
        items.forEach(it => {
          maxVal = Math.max(maxVal, getRightValue(it));
        });
        if (maxVal <= 0) maxVal = 1;

        const axisBottomPadding = 48;
        const legendH = 0; // legend moved below chart
        const chartH = Math.max(80, Math.min(maxH - axisBottomPadding - legendH, 320));
        const chartTop = y + legendH;
        const chartBottom = chartTop + chartH;
        const n = items.length;

        // spacing
        const groupGap = Math.max(8, Math.round(w * 0.02));
        const totalGroupGaps = groupGap * (n + 1);
        const groupAvailableW = Math.max(n, w - totalGroupGaps);
        const groupW = Math.floor(groupAvailableW / n);
        const barW = Math.max(10, Math.floor(groupW * 0.7));

        // baseline
        c.strokeStyle = opts.rightAxisColor || '#cbd5e1';
        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(x, chartBottom + 0.5);
        c.lineTo(x + w, chartBottom + 0.5);
        c.stroke();

        // grid lines & tick labels
        c.strokeStyle = opts.rightGridColor || 'rgba(15,23,42,0.06)';
        c.lineWidth = 1;
        c.font = `11px Inter, Arial, sans-serif`;
        c.fillStyle = opts.rightGridLabelColor || '#475569';
        c.textAlign = 'right';
        c.textBaseline = 'middle';
        const ticks = 3;
        for (let t = 0; t <= ticks; t++) {
          const yPos = chartTop + Math.round((chartH * t) / ticks);
          c.beginPath();
          c.moveTo(x, yPos + 0.5);
          c.lineTo(x + w, yPos + 0.5);
          c.stroke();
          const val = Math.round(maxVal * (1 - t / ticks));
          c.fillText(String(val), x - 8, yPos);
        }

        // draw bars
        let px = x + groupGap;
        c.textAlign = 'center';
        for (let i = 0; i < n; i++) {
          const it = items[i];
          const val = getRightValue(it);
          const hBar = Math.round((val / maxVal) * (chartH - 8));
          const bx = px + Math.floor((groupW - barW) / 2);
          const by = chartBottom - hBar;

          c.fillStyle = opts.rightBarColor || '#f97316';
          c.fillRect(bx, by, barW, hBar);

          // value label
          c.fillStyle = '#0f172a';
          c.font = `11px Inter, Arial, sans-serif`;
          c.textBaseline = 'bottom';
          c.fillText(String(val), bx + Math.round(barW / 2), Math.max(by - 4, by + 10));

          // label under bar - two lines justified within label width
          const label = String(it.municipio || '').trim();
          const maxLabelW = Math.max(28, groupW - 4);
          const labelLineH = 12;
          drawLabelTwoLinesJustified(c, label, bx + Math.round(barW / 2), chartBottom + 6, maxLabelW, labelLineH);

          // optionally show percentage to the right of bar if exists
          if (it.porcentaje) {
            c.textAlign = 'left';
            c.textBaseline = 'middle';
            c.fillText(String(it.porcentaje), bx + barW + 6, by + Math.round(hBar / 2));
          }

          px += groupW + groupGap;
        }

        // legend: centered below the chart (single item)
        try {
          c.font = `12px Inter, Arial, sans-serif`;
          const lab = 'Total hogares';
          const boxW = 12;
          const tW = c.measureText(lab).width;
          const totalW = boxW + 4 + tW;
          const lx = x + Math.round((w - totalW) / 2);
          const ly = chartBottom + 40; // 35px below chart bottom
          c.fillStyle = opts.rightBarColor || '#f97316';
          c.fillRect(lx, ly - 6, boxW, 12);
          c.fillStyle = opts.rightLegendColor || '#334155';
          c.textAlign = 'left';
          c.textBaseline = 'middle';
          c.fillText(lab, lx + boxW + 4, ly);
        } catch (e) {
          // ignore any measurement errors
        }

        return chartBottom + axisBottomPadding;
      };

      const rightChartMaxH = 500;
      const rightChartTextGap = typeof opts.rightChartTextGap === 'number' ? opts.rightChartTextGap : 100;
      // define the y where the right chart should start (after the right text area)
      const rightChartStartY = afterRightTextY + rightChartTextGap;
      // start the right chart at rightChartStartY
      const afterRightY = drawRightVerticalBars(ctx, rightDatos, chartX, rightChartStartY, chartAreaW, rightChartMaxH);

      // right chart caption
      const textoGraficoRight = dataRight.textoGrafico || '';
      if (textoGraficoRight) {
        ctx.fillStyle = opts.rightNoteColor || '#475569';
        ctx.font = `${opts.rightNoteSize || 20}px Inter, Arial, sans-serif`;
        const rightNoteLineH = Math.round((opts.rightNoteSize || 20) * 1.4);
        const rightNoteLines = wrapLines(ctx, textoGraficoRight, chartAreaW);
        // position caption below the chart area; use rightChartStartY (chart's top) and afterRightY (chart's bottom)
        drawJustifiedLines(ctx, rightNoteLines, chartX, Math.max(rightChartStartY + 50, afterRightY + 50), chartAreaW, rightNoteLineH);
      }


      // draw logo end-right
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      const logoPromises = [];
      const mainLogoPromise = mainLogoSrc
        ? new Promise((res) => {
            const img = new Image();
            if (/^https?:\/\//i.test(mainLogoSrc) || /^\/\//.test(mainLogoSrc)) {
              img.crossOrigin = 'anonymous';
            }
            img.onload = () => {
              try {
                const maxW = Math.min(200, Math.round(width * 0.18));
                const w = Math.min(maxW, img.width);
                const h = (img.height / img.width) * w;
                const lx = width - 16 - w;
                const ly = height - 16 - h;
                ctx.drawImage(img, lx, ly, w, h);
              } catch (e) { /* ignore */ }
              res();
            };
            img.onerror = () => res();
            img.src = mainLogoSrc;
          })
        : Promise.resolve();
      logoPromises.push(mainLogoPromise);

      Promise.all(logoPromises).then(() => resolve());
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_cuatro_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});